import {
  ERROR_CODES,
  LIMITS,
  type ClientMessage,
  type ErrorCode,
  type IceCandidate,
  type IceServer,
  type PeerDescriptor,
  type PeerRole,
  type ServerMessage,
  type Signal,
} from './messages';

export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

/**
 * Validators build a fresh object from the fields they checked, so unknown
 * properties never travel further than the parser.
 */
class Invalid extends Error {}

function fail(message: string): never {
  throw new Invalid(message);
}

type Obj = Record<string, unknown>;

function obj(value: unknown, what: string): Obj {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) fail(`${what} must be an object`);
  return value as Obj;
}

function str(value: unknown, what: string, max: number, min = 0): string {
  if (typeof value !== 'string') fail(`${what} must be a string`);
  if (value.length > max) fail(`${what} is too long`);
  if (value.length < min) fail(`${what} is too short`);
  return value;
}

const ID_PATTERN = /^[A-Za-z0-9_-]+$/;

function id(value: unknown, what: string): string {
  const s = str(value, what, LIMITS.maxIdLength, 1);
  if (!ID_PATTERN.test(s)) fail(`${what} has invalid characters`);
  return s;
}

/** A server-issued secret: an id that is long enough to hold 128 bits. */
function key(value: unknown, what: string): string {
  const s = id(value, what);
  if (s.length < LIMITS.minKeyLength) fail(`${what} is too short`);
  return s;
}

function bool(value: unknown, what: string): boolean {
  if (typeof value !== 'boolean') fail(`${what} must be a boolean`);
  return value;
}

function int(value: unknown, what: string, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
    fail(`${what} must be an integer between ${min} and ${max}`);
  }
  return value;
}

function finite(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(`${what} must be a finite number`);
  return value;
}

function oneOf<T extends string>(value: unknown, what: string, allowed: readonly T[]): T {
  if (typeof value !== 'string' || !allowed.includes(value as T)) fail(`${what} is not an accepted value`);
  return value as T;
}

function arr(value: unknown, what: string, max: number): unknown[] {
  if (!Array.isArray(value)) fail(`${what} must be an array`);
  if (value.length > max) fail(`${what} has too many entries`);
  return value;
}

const ROLES: readonly PeerRole[] = ['viewer', 'guest'];

function iceCandidate(value: unknown): IceCandidate {
  const o = obj(value, 'candidate');
  return {
    candidate: str(o.candidate, 'candidate.candidate', LIMITS.maxCandidateLength),
    sdpMid: o.sdpMid == null ? null : str(o.sdpMid, 'candidate.sdpMid', LIMITS.maxIdLength),
    sdpMLineIndex: o.sdpMLineIndex == null ? null : int(o.sdpMLineIndex, 'candidate.sdpMLineIndex', 0, 255),
    usernameFragment:
      o.usernameFragment == null ? null : str(o.usernameFragment, 'candidate.usernameFragment', 256),
  };
}

function signal(value: unknown): Signal {
  const o = obj(value, 'signal');
  switch (o.kind) {
    case 'offer':
    case 'answer':
      return {
        kind: o.kind,
        session: id(o.session, 'signal.session'),
        sdp: str(o.sdp, 'signal.sdp', LIMITS.maxSdpLength, 1),
        ...(o.seq === undefined ? {} : { seq: int(o.seq, 'signal.seq', 0, 2 ** 31) }),
      };
    case 'candidate':
      return {
        kind: 'candidate',
        session: id(o.session, 'signal.session'),
        candidate: o.candidate == null ? null : iceCandidate(o.candidate),
      };
    case 'restart':
      return { kind: 'restart', session: o.session == null ? null : id(o.session, 'signal.session') };
    case 'media-state':
      return {
        kind: 'media-state',
        video: bool(o.video, 'signal.video'),
        audio: bool(o.audio, 'signal.audio'),
      };
    case 'lobby':
    case 'admitted':
      return { kind: o.kind };
    default:
      return fail('unknown signal kind');
  }
}

function iceServer(value: unknown): IceServer {
  const o = obj(value, 'iceServer');
  const url = (u: unknown): string => {
    const s = str(u, 'iceServer.urls', LIMITS.maxUrlLength, 1);
    if (!/^(stun|stuns|turn|turns):/i.test(s)) fail('iceServer.urls must be stun: or turn: URLs');
    return s;
  };
  const urls = Array.isArray(o.urls)
    ? arr(o.urls, 'iceServer.urls', LIMITS.maxUrlsPerIceServer).map(url)
    : url(o.urls);
  if (Array.isArray(urls) && urls.length === 0) fail('iceServer.urls must not be empty');
  const out: IceServer = { urls };
  if (o.username !== undefined) out.username = str(o.username, 'iceServer.username', LIMITS.maxCredentialLength);
  if (o.credential !== undefined) {
    out.credential = str(o.credential, 'iceServer.credential', LIMITS.maxCredentialLength);
  }
  return out;
}

function iceServers(value: unknown): IceServer[] {
  return arr(value, 'iceServers', LIMITS.maxIceServers).map(iceServer);
}

function peerDescriptor(value: unknown): PeerDescriptor {
  const o = obj(value, 'peer');
  const base: PeerDescriptor = {
    peerId: id(o.peerId, 'peer.peerId'),
    role: oneOf(o.role, 'peer.role', ROLES),
    name: str(o.name, 'peer.name', LIMITS.maxNameLength),
  };
  return o.participantId === undefined ? base : { ...base, participantId: key(o.participantId, 'peer.participantId') };
}

function keyList(value: unknown, what: string): string[] {
  return arr(value, what, LIMITS.maxBannedListed).map((v) => key(v, what));
}

function clientMessage(value: unknown): ClientMessage {
  const o = obj(value, 'message');
  switch (o.type) {
    case 'open-room': {
      const hasRoom = o.roomId !== undefined;
      const hasKey = o.hostKey !== undefined;
      if (hasRoom !== hasKey) fail('roomId and hostKey must be given together');
      const base = {
        type: 'open-room' as const,
        v: int(o.v, 'v', 0, 1_000_000),
        maxViewers: int(o.maxViewers, 'maxViewers', 0, LIMITS.maxViewers),
        allowGuests: bool(o.allowGuests, 'allowGuests'),
        ...(o.relay === undefined ? {} : { relay: bool(o.relay, 'relay') }),
        ...(o.sharedLinkId === undefined ? {} : { sharedLinkId: key(o.sharedLinkId, 'sharedLinkId') }),
        ...(o.banned === undefined ? {} : { banned: keyList(o.banned, 'banned') }),
      };
      return hasRoom ? { ...base, roomId: id(o.roomId, 'roomId'), hostKey: key(o.hostKey, 'hostKey') } : base;
    }
    case 'update-room':
      return {
        type: 'update-room',
        ...(o.allowGuests === undefined ? {} : { allowGuests: bool(o.allowGuests, 'allowGuests') }),
        ...(o.sharedLinkId === undefined
          ? {}
          : { sharedLinkId: o.sharedLinkId === null ? null : key(o.sharedLinkId, 'sharedLinkId') }),
        ...(o.ban === undefined ? {} : { ban: keyList(o.ban, 'ban') }),
      };
    case 'create-invite':
      return {
        type: 'create-invite',
        requestId: id(o.requestId, 'requestId'),
        subject: key(o.subject, 'subject'),
        shared: bool(o.shared, 'shared'),
        expiresAt: int(o.expiresAt, 'expiresAt', 0, 8_640_000_000_000_000),
      };
    case 'join': {
      const base = {
        type: 'join' as const,
        v: int(o.v, 'v', 0, 1_000_000),
        roomId: id(o.roomId, 'roomId'),
        role: oneOf(o.role, 'role', ROLES),
        name: str(o.name, 'name', LIMITS.maxNameLength),
      };
      return {
        ...base,
        ...(o.peerKey === undefined ? {} : { peerKey: key(o.peerKey, 'peerKey') }),
        ...(o.inviteToken === undefined ? {} : { inviteToken: key(o.inviteToken, 'inviteToken') }),
      };
    }
    case 'signal': {
      const s = signal(o.signal);
      return o.to === undefined ? { type: 'signal', signal: s } : { type: 'signal', to: id(o.to, 'to'), signal: s };
    }
    case 'kick': {
      return {
        type: 'kick',
        peerId: id(o.peerId, 'peerId'),
        ...(o.reason === undefined ? {} : { reason: str(o.reason, 'reason', LIMITS.maxReasonLength) }),
        ...(o.ban === undefined ? {} : { ban: bool(o.ban, 'ban') }),
      };
    }
    case 'close-room':
      return { type: 'close-room' };
    case 'leave':
      return { type: 'leave' };
    case 'ping':
      return { type: 'ping', t: finite(o.t, 't') };
    default:
      return fail('unknown message type');
  }
}

function serverMessage(value: unknown): ServerMessage {
  const o = obj(value, 'message');
  switch (o.type) {
    case 'room-opened': {
      const base = {
        type: 'room-opened' as const,
        roomId: id(o.roomId, 'roomId'),
        hostKey: key(o.hostKey, 'hostKey'),
        reclaimed: bool(o.reclaimed, 'reclaimed'),
        peers: arr(o.peers, 'peers', LIMITS.maxPeersListed).map(peerDescriptor),
      };
      return o.iceServers === undefined ? base : { ...base, iceServers: iceServers(o.iceServers) };
    }
    case 'joined': {
      const base = {
        type: 'joined' as const,
        peerId: id(o.peerId, 'peerId'),
        peerKey: key(o.peerKey, 'peerKey'),
        hostPresent: bool(o.hostPresent, 'hostPresent'),
      };
      return {
        ...base,
        ...(o.participantId === undefined ? {} : { participantId: key(o.participantId, 'participantId') }),
        ...(o.inviteToken === undefined ? {} : { inviteToken: key(o.inviteToken, 'inviteToken') }),
        ...(o.iceServers === undefined ? {} : { iceServers: iceServers(o.iceServers) }),
      };
    }
    case 'room-updated':
      return {
        type: 'room-updated',
        allowGuests: bool(o.allowGuests, 'allowGuests'),
        sharedLinkId: o.sharedLinkId === null ? null : key(o.sharedLinkId, 'sharedLinkId'),
      };
    case 'invite':
      return {
        type: 'invite',
        requestId: id(o.requestId, 'requestId'),
        token: key(o.token, 'token'),
        expiresAt: int(o.expiresAt, 'expiresAt', 0, 8_640_000_000_000_000),
      };
    case 'peer-joined':
      return { type: 'peer-joined', peer: peerDescriptor(o.peer) };
    case 'peer-left':
      return {
        type: 'peer-left',
        peerId: id(o.peerId, 'peerId'),
        reason: oneOf(o.reason, 'reason', ['left', 'disconnected', 'kicked'] as const),
      };
    case 'signal': {
      const s = signal(o.signal);
      return o.from === undefined
        ? { type: 'signal', signal: s }
        : { type: 'signal', from: id(o.from, 'from'), signal: s };
    }
    case 'host-left':
      return { type: 'host-left' };
    case 'host-joined':
      return { type: 'host-joined' };
    case 'kicked':
      return { type: 'kicked', reason: str(o.reason, 'reason', LIMITS.maxReasonLength) };
    case 'room-closed':
      return { type: 'room-closed', reason: oneOf(o.reason, 'reason', ['closed', 'expired'] as const) };
    case 'error': {
      const base = {
        type: 'error' as const,
        code: oneOf<ErrorCode>(o.code, 'code', ERROR_CODES),
        message: str(o.message, 'message', LIMITS.maxErrorMessageLength),
      };
      return o.peerId === undefined ? base : { ...base, peerId: id(o.peerId, 'peerId') };
    }
    case 'pong':
      return { type: 'pong', t: finite(o.t, 't') };
    default:
      return fail('unknown message type');
  }
}

function guard<T>(parse: (value: unknown) => T): (value: unknown) => ParseResult<T> {
  return (value) => {
    try {
      return { ok: true, value: parse(value) };
    } catch (err) {
      if (err instanceof Invalid) return { ok: false, error: err.message };
      throw err;
    }
  };
}

export const parseClientMessage = guard(clientMessage);
export const parseServerMessage = guard(serverMessage);
export const parseIceServers = guard(iceServers);

/** Decode one text frame: size check, JSON parse, then shape validation. */
export function decodeFrame<T>(text: string, parse: (value: unknown) => ParseResult<T>): ParseResult<T> {
  if (text.length > LIMITS.maxMessageBytes) return { ok: false, error: 'message is too large' };
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return { ok: false, error: 'message is not valid JSON' };
  }
  return parse(json);
}
